<script setup lang="ts">
import type { Option } from '#imports'
import { isTest } from '#imports'

const props = defineProps<{
  option?: Option
  title?: string
  quantity?: number
  mode?: 'buy' | 'add' | 'quantity'
  readonly?: boolean
  displaySymbol?: boolean
}>()

const emit = defineEmits<{
  (event: 'select'): void
  (event: 'increment'): void
  (event: 'decrement'): void
  (event: 'change', value: number): void
}>()

const mediaQuery = useMediaQuery()

const isSoldOut = computed(
  () => !isTest() && (!props.option || props.option.stock === 0),
)

function change(e: CustomEvent) {
  emit('change', e.detail.value)
}
</script>

<template>
  <ion-card
    v-if="option"
    class="os-option-card"
    :class="{
      'os-option-card--buy': mode === 'buy',
      'os-option-card--add': (mode ?? 'add') === 'add',
      'os-option-card--quantity': mode === 'quantity',
      'os-option-card--readonly': readonly,
      'os-option-card--compact': !mediaQuery.isMedium,
    }"
    @click="emit('select')"
    :inert="isSoldOut"
  >
    <os-row cross-axis-alignment="stretch" gap="none">
      <img
        v-if="mode === 'quantity' && option.image"
        class="os-option-card__image deal-image-ratio"
        :alt="option.name"
        :src="option.image.lg"
        :style="mediaQuery.isSmall ? 'width: 25%' : 'width: 40%'"
      />

      <os-expanded>
        <os-column
          cross-axis-alignment="stretch"
          gap="narrow"
          class="os-option-card__details"
        >
          <ion-card-title>
            <os-clamp :lines="2" v-if="title"
              ><b>{{ stripTags(title) }}</b></os-clamp
            >
            <os-clamp :lines="2">{{ stripTags(option?.name) }}</os-clamp>
            <div
              class="os-option-card__variant-name"
              v-if="option?.variant && option?.name !== option?.variant"
            >
              {{ stripTags(option?.variant) }}
            </div>
          </ion-card-title>

          <os-expanded />

          <template v-if="mediaQuery.isLarge">
            <os-row gap="narrow" cross-axis-alignment="end">
              <os-column cross-axis-alignment="start" gap="none">
                <span v-if="option?.openGdsRateId">{{ $t('deal.from') }}</span>
                <s
                  class="h3"
                  v-if="
                    option.originalPrice &&
                    parseFloat(option.originalPrice) >
                      parseFloat(option.discountPrice)
                  "
                  ><os-currency
                    :value="option.originalPrice"
                    :display-symbol="displaySymbol"
                /></s>
                <div class="h1">
                  <os-currency
                    :value="option.discountPrice"
                    :display-symbol="displaySymbol"
                  />
                </div>
              </os-column>

              <os-expanded />

              <ion-button
                secondary
                inert
                size="small"
                v-if="mode !== 'quantity' && option.percentOff && !isSoldOut"
              >
                <b><os-percent :value="option.percentOff * -1" /></b>
              </ion-button>

              <ion-button
                size="small"
                v-if="mode && (mode === 'add' || mode === 'buy')"
              >
                <template v-if="isSoldOut" />
                <ion-icon
                  v-else-if="mode === 'add'"
                  slot="end"
                  :icon="ioniconsAddOutline"
                ></ion-icon>
                <ion-icon
                  v-else-if="mode === 'buy'"
                  slot="end"
                  :icon="ioniconsChevronForward"
                ></ion-icon>
                <b v-if="isSoldOut">{{ $t('label.sold_out') }}</b>
                <b v-else-if="mode === 'add'">{{
                  $t(option.openGdsRateId ? 'label.book' : 'label.add')
                }}</b>
                <b v-else-if="mode === 'buy'">{{
                  $t(option.openGdsRateId ? 'label.book' : 'label.order_now')
                }}</b>
              </ion-button>

              <template v-if="mode === 'quantity'" gap="none">
                <ion-button
                  v-if="!readonly"
                  class="os-option-card__decrement"
                  size="small"
                  @click="emit('decrement')"
                >
                  <ion-icon
                    slot="icon-only"
                    :icon="ioniconsRemoveOutline"
                  ></ion-icon>
                </ion-button>

                <ion-button size="small" class="os-option-card__quantity" inert>
                  <template v-if="!!readonly">&times;&nbsp;</template>
                  <b>{{ quantity ?? 0 }}</b>
                </ion-button>

                <ion-button
                  v-if="!readonly"
                  class="os-option-card__increment"
                  size="small"
                  @click="emit('increment')"
                >
                  <ion-icon
                    slot="icon-only"
                    :icon="ioniconsAddOutline"
                  ></ion-icon>
                </ion-button>
              </template>
            </os-row>
          </template>

          <template v-else>
            <os-row
              main-axis-alignment="space-between"
              cross-axis-alignment="center"
              gap="none"
            >
              <os-expanded>
                <os-row
                  cross-axis-alignment="center"
                  main-axis-alignment="start"
                  gap="narrow"
                >
                  <span v-if="option?.openGdsRateId">{{
                    $t('deal.from')
                  }}</span>
                  <s
                    class="h3"
                    v-if="
                      option.originalPrice &&
                      parseFloat(option.originalPrice) >
                        parseFloat(option.discountPrice)
                    "
                    ><os-currency
                      :value="option.originalPrice"
                      :display-symbol="displaySymbol"
                  /></s>
                  <div class="h1">
                    &nbsp;<os-currency
                      :value="option.discountPrice"
                      :display-symbol="displaySymbol"
                    />
                  </div>
                </os-row>
              </os-expanded>

              <os-column
                class="os-option-card__percent-off"
                v-if="option.percentOff && !isSoldOut"
              >
                <os-percent :value="option.percentOff * -1" />
              </os-column>
            </os-row>

            <ion-button
              size="small"
              v-if="mode && (mode === 'add' || mode === 'buy')"
            >
              <b v-if="isSoldOut">{{ $t('label.sold_out') }}</b>
              <template v-else>
                <b v-if="isSoldOut">{{ $t('label.sold_out') }}</b>
                <b v-else-if="mode === 'add'">{{ $t('label.add') }}</b>
                <b v-else-if="mode === 'buy'">{{ $t('label.order_now') }}</b>
                <ion-icon
                  v-if="mode === 'add'"
                  :slot="option.percentOff ? 'end' : 'icon-only'"
                  :icon="ioniconsAddOutline"
                ></ion-icon>
                <ion-icon
                  v-else-if="mode === 'buy'"
                  :slot="option.percentOff ? 'end' : 'icon-only'"
                  :icon="ioniconsChevronForward"
                ></ion-icon>
              </template>
            </ion-button>

            <template v-if="mode === 'quantity'">
              <os-row
                main-axis-alignment="end"
                cross-axis-alignment="stretch"
                gap="none"
              >
                <ion-button
                  v-if="!readonly"
                  class="os-option-card__decrement"
                  size="small"
                  @click="emit('decrement')"
                >
                  <ion-icon
                    slot="icon-only"
                    :icon="ioniconsRemoveOutline"
                  ></ion-icon>
                </ion-button>

                <os-expanded style="margin-right: -1rem">
                  <ion-button
                    size="small"
                    class="os-option-card__quantity"
                    style="
                      width: 100%;
                      max-width: initial;
                      height: 100% !important;
                    "
                    inert
                  >
                    <template v-if="!!readonly">&times;&nbsp;</template>
                    <b>{{ quantity ?? 0 }}</b>
                  </ion-button>
                </os-expanded>

                <ion-button
                  v-if="!readonly"
                  class="os-option-card__increment"
                  size="small"
                  @click="emit('increment')"
                >
                  <ion-icon
                    slot="icon-only"
                    :icon="ioniconsAddOutline"
                  ></ion-icon>
                </ion-button>
              </os-row>
            </template>
          </template>
        </os-column>
      </os-expanded>
    </os-row>
  </ion-card>
</template>

<style scoped>
.os-option-card {
  background-color: var(--background--primary);
}

.os-option-card:not(.os-option-card--quantity) {
  cursor: pointer;
}

.os-option-card[inert] {
  opacity: 0.5;
}

.os-option-card > .os-row {
  height: 100%;
}

.os-option-card__image {
  object-fit: contain;
  background-color: white;
  display: flex;
  flex: 0 0 0%;
  flex-basis: auto;
}

.os-option-card__details {
  padding: 1rem;
}

.os-option-card--compact .os-option-card__details {
  padding: 0.75rem 1rem;
}

.os-option-card__variant-name {
  opacity: 0.75;
  font-weight: normal;
}

.os-option-card__quantity {
  min-width: 2rem;
  max-width: 5ch;
  width: auto;

  min-height: auto;
  max-height: auto;
  height: 28.08px;

  font-weight: bold;
  font-size: 1.075rem;

  --padding-start: 0;
  --padding-end: 0;
  --border-radius: 0;

  margin-left: -0.5rem;
  margin-right: -0.5rem;

  position: relative;
  z-index: 1;
}

.os-option-card--compact .os-option-card__quantity {
  height: 29.375px !important;
}

.os-option-card--readonly .os-option-card__quantity {
  --border-radius: 0.25rem;
  margin-left: 0;
  margin-right: 0;
}

.os-option-card__percent-off {
  background-color: var(--header);
  color: var(--white);
  border-radius: 0.25rem;
  padding: 5px;
  flex-shrink: 1;
  flex-grow: 0;
  font-size: 0.65rem;
  font-weight: bold;
}
</style>
